import { Outlet } from 'react-router-dom';

import AuthScreen from '../auth/AuthScreen.jsx';
import SessionGate from '../session/SessionGate.jsx';
import ErrorBoundary from '../ErrorBoundary.jsx';
import ConnectionBanner from '../transport/ConnectionBanner.jsx';
import LoadingScreen from '../components/LoadingScreen.jsx';
import { useAuth } from '../auth/AuthenticationContext.jsx';
import MenuProvider from './MenuProvider.jsx';

// Auth gate for the SPA's private surface, expressed as a layout route: it renders
// the signed-in screens (its <Outlet/>) only when authenticated, and wraps every
// one of them in the untrusted-data ErrorBoundary and the SessionGate. Registering
// it as a single pathless parent route means each private screen inherits both
// without repeating them.
//
// Public routes (/privacy-policy, /legal, /password-code) are registered as
// siblings, OUTSIDE this element, so they stay reachable when signed out. This is
// the same gate logic the home route carried before the app-style navigation —
// lifted up so it now covers the Property, Zone and action screens too.
export default function GatedLayout() {
  const { loaded, authenticated } = useAuth();

  // The auth session has not resolved yet, so neither the login screen nor a
  // Property skeleton would be right; a neutral spinner covers the moment.
  if (!loaded) {
    return <LoadingScreen />;
  }

  if (!authenticated) {
    return <AuthScreen />;
  }

  // The ErrorBoundary is the last line of defence for the read model: untrusted
  // device frames can hold malformed shapes, and a render-phase throw on any
  // screen would otherwise blank the whole app. SessionGate decides whether to
  // show the screen, an account-setup notice, a session-error overlay, or a brief
  // "checking your account" placeholder.
  return (
    <ErrorBoundary>
      {/* The burger menu's drawer state, above every screen and the loading
          skeletons that stand in for them, so an open drawer survives a
          skeleton being replaced by its screen (see MenuProvider). */}
      <MenuProvider>
        <SessionGate>
          {/* Connection status on EVERY signed-in screen, not just Property — a
              dropped connection (and that it won't recover on its own) must be
              visible wherever the user is. The slot collapses when the banner
              has nothing to say (see .connection-banner-slot:empty). */}
          <div className="connection-banner-slot">
            <ConnectionBanner />
          </div>
          <Outlet />
        </SessionGate>
      </MenuProvider>
    </ErrorBoundary>
  );
}
